import { FC } from "react";
import { Box, Tooltip, styled } from "@mui/material";
import { useLabelsVisibility } from "@/shared/hooks";
import { DEFAULT_TIMECODE_ICON, ICON_MAP } from "./timecodeIcons";
import type { PlayerTimecode } from "./playerTimecode";

// Порт референсного VodSeekBar/TimecodeMarkers.js: иконки-«пометки» (MARKER)
// поверх сикбара — тултип с заголовком/описанием/временем, клик перематывает.
const Root = styled("div")({
  position: "absolute",
  top: -15,
  left: 0,
  right: 0,
  height: 24,
  pointerEvents: "none",
  zIndex: 10,
  display: "flex",
  alignItems: "center",
});

const Marker = styled("div")(({ theme }) => ({
  position: "absolute",
  cursor: "pointer",
  pointerEvents: "auto",
  transition: "all 0.2s ease",
  transform: "translate(-50%, -50%)",
  top: "50%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  opacity: 0.6,
  "&:hover": {
    opacity: 1,
    transform: "translate(-50%, -50%) scale(1.1)",
    "& .TimecodeMarkers-icon": {
      backgroundColor: "#f8f8ff",
      boxShadow: `0 2px 12px rgba(0,0,0,0.2), 0 0 8px ${theme.palette.primary.main}40`,
    },
    "& .TimecodeMarkers-icon svg": {
      color: theme.palette.primary.main,
    },
  },
}));

const MarkerIcon = styled("div")({
  width: 18,
  height: 18,
  backgroundColor: "white",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
  marginBottom: 2,
  "& svg": {
    fontSize: 14,
    color: "#666666",
    transition: "color 0.2s ease",
  },
});

const MarkerArrow = styled("div")({
  width: 0,
  height: 0,
  borderLeft: "3px solid transparent",
  borderRight: "3px solid transparent",
  borderTop: "3px solid white",
  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
});

interface TimecodeMarkersProps {
  timecodes?: PlayerTimecode[] | null;
  duration: number;
  onTimecodeClick: (time: number) => void;
}

export const TimecodeMarkers: FC<TimecodeMarkersProps> = ({
  timecodes,
  duration,
  onTimecodeClick,
}) => {
  const [labelsVisible] = useLabelsVisibility();

  if (!duration || !timecodes || timecodes.length === 0 || !labelsVisible) {
    return null;
  }

  // Только MARKER (аннотации отображаются отдельным оверлеем) и в пределах
  // длительности видео — как в референсе.
  const timecodesToRender = timecodes.filter(
    (timecode) =>
      timecode.time <= duration &&
      (!timecode.type || timecode.type === "MARKER"),
  );

  return (
    <Root>
      {timecodesToRender.map((timecode) => {
        const leftPosition = (timecode.time / duration) * 100;
        const IconComponent = ICON_MAP[timecode.icon] ?? DEFAULT_TIMECODE_ICON;

        return (
          <Tooltip
            key={timecode.id}
            title={
              <div>
                <Box sx={{ fontWeight: "bold" }}>{timecode.title}</Box>
                {timecode.description && (
                  <Box sx={{ fontSize: "0.8em" }}>{timecode.description}</Box>
                )}
                <Box sx={{ fontSize: "0.7em", opacity: 0.8 }}>
                  {Math.floor(timecode.time / 60)}:
                  {String(Math.floor(timecode.time % 60)).padStart(2, "0")}
                </Box>
              </div>
            }
            placement="top"
            arrow
            PopperProps={{ style: { zIndex: 9999 } }}
          >
            <Marker
              style={{ left: `${leftPosition}%` }}
              onClick={() => onTimecodeClick(timecode.time)}
            >
              <MarkerIcon className="TimecodeMarkers-icon">
                <IconComponent />
              </MarkerIcon>
              <MarkerArrow />
            </Marker>
          </Tooltip>
        );
      })}
    </Root>
  );
};
